import AddToQueueIcon from '@mui/icons-material/AddToQueue';
import {
  Autocomplete, Box, Button, Dialog, DialogActions,
  DialogContent,
  DialogContentText, DialogTitle, Grid, IconButton, TextField, Tooltip
} from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { addProductToOrderGuides } from '../../../../api/catalog';
import { getProductOrderGuide } from '../../../../redux/slices/catalog';
import { useDispatch, useSelector } from '../../../../redux/store';
import { AwaitButton } from '../../../../reusable-components/await-button';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import { useSnackbar } from '../../../../reusable-components/snackbar';

const categoryInputStyles = {
  '& legend': { display: 'none' },
  '& fieldset': { top: 0 },
  '& .MuiFormLabelRoot ': 'none',
  width: '100%',
};

OrderHistoryDialog.propTypes = {
  productId: PropTypes.number,
  agoraCategoryId: PropTypes.number,
  deactivated: PropTypes.bool,
};

export default function OrderHistoryDialog({ productId, agoraCategoryId, deactivated }) {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState([]);
  const { data: { productOrderGuide, agoraCategories, agoraSubcategories } } = useSelector((state) => state.catalog);
  const isLoading = useSelector((state) => state.catalog.isLoading);
  const [value, setValue] = useState(agoraCategories.filter((item) => agoraCategoryId === item.id)[0]);
  const [category, setCategory] = useState(null);
  const [subcategory, setSubcategory] = useState(null);
  const isExistingCategory = category?.id === productOrderGuide.agoraCategoryId ||
    productOrderGuide.productCategories?.some(x => x.agoraCategoryId === category?.id);

  useEffect(() => {
    setData(productOrderGuide.orderGuides);
    setSelected(productOrderGuide.orderGuides?.filter(og => og.hasItem)?.map(og => og.orderGuideId) ?? [])
  }, [productOrderGuide.orderGuides]);

  useEffect(() => {
    if (!open) return;
    setCategory(agoraCategories.find(x => x.id === productOrderGuide.agoraCategoryId) ?? null);
    setSubcategory(agoraSubcategories.find(x => x.id === productOrderGuide.agoraSubcategoryId) ?? null);
  }, [open, agoraCategories, agoraSubcategories, productOrderGuide.agoraCategoryId, productOrderGuide.agoraSubcategoryId]);

  useEffect(() => {
    if (open) {
      dispatch(getProductOrderGuide(productId, agoraCategoryId));
    }
  }, [agoraCategoryId, dispatch, open, productId]);

  const handleClickOpen = (e) => {
    e.addToOrderGuide = true;
    setValue(agoraCategories.find(item => item.id === agoraCategoryId));
    setOpen(true);
  };

  const handleClose = () => {
    setData([]);
    setOpen(false);
  };

  const handleSave = async () => {
    if (isLoading || !category || !subcategory) return;
    const response = await addProductToOrderGuides(selected, productId, value.id,
      category.id === value.id && !isExistingCategory ? subcategory.id : 0);
    if (response.data === true) {
      handleClose();
    } else {
      enqueueSnackbar('Unable to update order guides', { variant: 'error' });
    }
  };

  const handleChangeCategory = (newValue) => {
    setValue(newValue);
    setCategory(agoraCategories.find(x => x.id === productOrderGuide.agoraCategoryId) ?? null);
    setSubcategory(agoraSubcategories.find(x => x.id === productOrderGuide.agoraSubcategoryId) ?? null);
    dispatch(getProductOrderGuide(productId, newValue.id));
  };

  const handleChangeItemCategory = (newValue) => {
    setCategory(newValue);
    const existingSubcategoryId = newValue.id === productOrderGuide.agoraCategoryId
      ? productOrderGuide.agoraSubcategoryId
      : productOrderGuide.productCategories?.find(x => x.agoraCategoryId === newValue.id)?.agoraSubcategoryId;
    setSubcategory(agoraSubcategories.find(x => x.id === existingSubcategoryId) ?? null);
  };

  const ORDER_GUIDE = [
    {
      field: 'facility',
      headerName: 'Facility',
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.row.facilityRestricted ? "Vendor restricted for this facility" : ""} arrow placement="top">
          <Box component="span" sx={{pointerEvents: 'auto', width: '100%'}}>
            <Box
              component="div"
              sx={{
                fontSize: '0.7rem',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'pre-wrap',
              }}
            >
              {params.value}
            </Box>
          </Box>
        </Tooltip>
      ),
      type: 'customSelect',
    },
    {
      field: 'company',
      headerName: 'Group',
      flex: 2,
      renderCell: (params) => (
        <Tooltip title={params.row.facilityRestricted ? "Vendor restricted for this facility" : ""} arrow placement="top">
          <Box component="span" sx={{pointerEvents: 'auto', width: '100%'}}>
            <Box
              component="div"
              sx={{
                fontSize: '0.7rem',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'pre-wrap',
                width: '100%'
              }}
            >
              {params.value || '\u00A0'}
            </Box>
          </Box>
        </Tooltip>
      ),
      type: 'customSelect',
    },
  ];
  const CustomLeftToolbar = () => (
    <DialogContentText sx={{ fontWeight: 'bold', mt: 2 }}>Add/Remove a facility</DialogContentText>
  );

  return (
    <div>
      <Tooltip title={deactivated ? 'Deactivated items cannot be added to order guides' : 'Add To Order Guide'} placement="bottom" arrow>
        <span>
          <IconButton color="info" size="small" disabled={deactivated} onClick={handleClickOpen}>
            <AddToQueueIcon />
          </IconButton>
        </span>
      </Tooltip>
      <Dialog open={open} onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose();
      }} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            textAlign: 'center',
          }}
        >
          Add To Order Guides
          <CloseIconButton onClick={handleClose}/>
        </DialogTitle>

        <DialogContent sx={{ height: '100%', width: '100%' }}>
          <DialogContentText sx={{ fontWeight: 'bold' }}>Select Order Guide</DialogContentText>{' '}
          <Autocomplete
            sx={{
              mt: 1,
              '& legend': { display: 'none' },
              '& fieldset': { top: 0 },
              '& .MuiFormLabelRoot ': 'none',
            }}
            type="new-password"
            size="small"
            disableClearable
            options={agoraCategories}
            disabled={isLoading}
            autoHighlight
            getOptionLabel={(option) => option?.name}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            value={value}
            onChange={(event, newValue) => {
              handleChangeCategory(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                name=""
                variant="outlined"
                autoComplete="off"
                inputProps={{
                  ...params.inputProps,
                }}
              />
            )}
          />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <DialogContentText sx={{ fontWeight: 'bold' }}>Choose/Change Category *</DialogContentText>
              <Autocomplete
                size="small"
                disableClearable
                sx={categoryInputStyles}
                type="new-password"
                options={agoraCategories.filter(x => x.id === productOrderGuide.agoraCategoryId || x.id === value?.id)}
                getOptionDisabled={(option) => option.id !== productOrderGuide.agoraCategoryId &&
                  !productOrderGuide.productCategories?.some(x => x.agoraCategoryId === option.id) &&
                  !productOrderGuide.canAddCategory}
                autoHighlight
                getOptionLabel={(option) => option?.name || ''}
                isOptionEqualToValue={(option, selectedValue) => option.id === selectedValue.id}
                value={category}
                onChange={(event, newValue) => handleChangeItemCategory(newValue)}
                renderInput={(params) => <TextField {...params} size="small" label="" variant="outlined" autoComplete="off" />}
              />
            </Grid>
            <Grid item xs={6}>
              <DialogContentText sx={{ fontWeight: 'bold' }}>Choose/Change Subcategory *</DialogContentText>
              <Autocomplete
                size="small"
                disableClearable
                sx={categoryInputStyles}
                type="new-password"
                options={agoraSubcategories.filter((item) => item.mainCategoryId === category?.id)}
                disabled={Boolean(isExistingCategory)}
                autoHighlight
                getOptionLabel={(option) => option?.name}
                isOptionEqualToValue={(option, selectedValue) => option.id === selectedValue.id}
                value={subcategory}
                onChange={(event, newValue) => setSubcategory(newValue)}
                renderInput={(params) => <TextField {...params} size="small" label="" variant="outlined" autoComplete="off" required />}
              />
            </Grid>
          </Grid>
          <Grid item xs={12} sx={{ mt: 1 }}>
            <CustomDataGrid
              boxSX={{ width: '100%', height: 'calc(100vh - 310px)' }}
              checkboxSelection
              CustomLeftToolbar={CustomLeftToolbar}
              data={data}
              displayCheckboxRight
              forceReRender={data}
              getRowId={(row) => row.orderGuideId}
              getRowClassName={(params) => (params.row.facilityRestricted ? 'disabled' : '')}
              gridColumns={ORDER_GUIDE}
              gridId="admin-catalog-order-guide"
              isModal
              isLoading={isLoading}
              keepNonExistentRowsSelected
              onRowSelectionModelChange={(newSelection) => {
                const newOrderGuidesWithOutDisabled = data?.filter((item) => !item.facilityRestricted)
                  .map((item) => ({ ...item, hasItem: newSelection.includes(item.orderGuideId) }));
                setData(newOrderGuidesWithOutDisabled);
                setSelected(newSelection);
              }}
              rowSelectionModel={selected}
              sort={[{ field: 'facility', sort: 'asc' }]}
              checkboxTooltipText="Vendor restricted for this facility"
              checkboxTooltipCheck={(row) => row.facilityRestricted}
              isRowSelectable={(params) => !params.row.facilityRestricted}
              sx={{
                '& .disabled': {
                  pointerEvents: 'none',
                  color: 'rgba(0, 0, 0,0.65)',
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                },
              }}
            />
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="error" size="medium" sx={{ width: 150, mr: 2 }} onClick={handleClose}>
            Cancel
          </Button>
          <AwaitButton variant="contained" color="primary" size="medium" sx={{ width: 150 }} onClick={handleSave} disabled={isLoading || !category || !subcategory}>
            Save
          </AwaitButton>
        </DialogActions>
      </Dialog>
    </div>
  );
}
